import React, { useState, useEffect } from 'react';
import { restaurantInvoiceAPI } from '../../services/api';

// Theme colors
const theme = {
  primaryColor: '#FF6961',
  primaryDark: '#E6524A',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  backgroundColor: '#F8FAFC'
};

// Invoice type display mapping
const INVOICE_TYPE_LABELS = {
  section9_5_restaurant: 'Food Invoice (Section 9(5))',
  customer_food: 'Food Invoice',
  customer_platform_fee: 'Platform Fee Invoice',
  subscription: 'Monthly Subscription',
  advertising_marketing: 'Advertising/Marketing',
  tds_statement: 'TDS Statement',
  settlement_statement: 'Settlement Statement',
  settlement_fee: 'Settlement Fee',
  platform_service_fee: 'Platform Fee',
  credit_note: 'Credit Note',
  debit_note: 'Debit Note'
};

// Invoice type badge colors
const INVOICE_TYPE_COLORS = {
  section9_5_restaurant: { bg: 'rgba(6, 182, 212, 0.1)', text: '#06b6d4' },
  customer_food: { bg: 'rgba(6, 182, 212, 0.1)', text: '#06b6d4' },
  customer_platform_fee: { bg: 'rgba(168, 85, 247, 0.1)', text: '#a855f7' },
  subscription: { bg: 'rgba(79, 70, 229, 0.1)', text: '#4f46e5' },
  advertising_marketing: { bg: 'rgba(245, 158, 11, 0.1)', text: '#f59e0b' },
  tds_statement: { bg: 'rgba(16, 185, 129, 0.1)', text: '#10b981' },
  settlement_statement: { bg: 'rgba(139, 92, 246, 0.1)', text: '#8b5cf6' },
  settlement_fee: { bg: 'rgba(236, 72, 153, 0.1)', text: '#ec4899' },
  platform_service_fee: { bg: 'rgba(239, 68, 68, 0.1)', text: '#ef4444' },
  credit_note: { bg: 'rgba(34, 197, 94, 0.1)', text: '#22c55e' },
  debit_note: { bg: 'rgba(249, 115, 22, 0.1)', text: '#f97316' }
};

// Default tax rates (centralized from backend TaxCalculator)
// These are fallbacks - actual rates come from invoice data or backend settings
const DEFAULT_TAX_RATES = {
  CGST_RATE: 9,      // CGST for intra-state (half of total GST)
  SGST_RATE: 9,      // SGST for intra-state (half of total GST)
  IGST_RATE: 18,     // IGST for inter-state (full GST)
  TDS_RATE: 0.1      // TDS under Section 194-O (0.1% effective Oct 1, 2024)
};

// Helper to extract tax rate from invoice data
const getInvoiceTaxRate = (invoice, taxType) => {
  // Try to get rate from invoice's line items first
  if (invoice?.lineItems?.[0]?.[taxType]?.rate !== undefined) {
    return invoice.lineItems[0][taxType].rate;
  }
  // Try direct rate property on invoice
  if (invoice?.[`${taxType}Rate`] !== undefined) {
    return invoice[`${taxType}Rate`];
  }
  // Fall back to centralized defaults
  switch (taxType) {
    case 'cgst': return DEFAULT_TAX_RATES.CGST_RATE;
    case 'sgst': return DEFAULT_TAX_RATES.SGST_RATE;
    case 'igst': return DEFAULT_TAX_RATES.IGST_RATE;
    case 'tds': return DEFAULT_TAX_RATES.TDS_RATE;
    default: return 0;
  }
};

function Invoices() {
  const [activeTab, setActiveTab] = useState('all');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [activeTab, dateRange]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      const params = {
        ...(dateRange.startDate && { startDate: dateRange.startDate }),
        ...(dateRange.endDate && { endDate: dateRange.endDate })
      };

      switch (activeTab) {
        case 'subscription':
          response = await restaurantInvoiceAPI.getAll({ ...params, type: 'subscription' });
          break;
        case 'advertising':
          response = await restaurantInvoiceAPI.getAll({ ...params, type: 'advertising_marketing' });
          break;
        case 'platform_fee':
          response = await restaurantInvoiceAPI.getAll({ ...params, type: 'platform_service_fee' });
          break;
        case 'tds':
          response = await restaurantInvoiceAPI.getTDSStatements(params);
          break;
        case 'settlements':
          response = await restaurantInvoiceAPI.getSettlementStatements(params);
          break;
        case 'credit_notes':
          response = await restaurantInvoiceAPI.getAll({ ...params, type: 'credit_note' });
          break;
        case 'debit_notes':
          response = await restaurantInvoiceAPI.getAll({ ...params, type: 'debit_note' });
          break;
        case 'all':
        default:
          response = await restaurantInvoiceAPI.getAll(params);
          break;
      }

      // Handle different API response structures with robust null checking
      if (!response || !response.data) {
        console.warn('No data in response:', response);
        setInvoices([]);
        return;
      }

      const data = response.data?.data || response.data;
      let invoicesList = [];

      if (Array.isArray(data)) {
        invoicesList = data;
      } else if (data?.invoices && Array.isArray(data.invoices)) {
        invoicesList = data.invoices;
      } else if (data?.statements && Array.isArray(data.statements)) {
        invoicesList = data.statements;
      } else {
        console.warn('Unexpected data structure:', data);
      }

      setInvoices(invoicesList);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      setError('Failed to load invoices. Please try again.');
      setInvoices([]); // Ensure invoices is always an array
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceDetails = async (invoiceId) => {
    try {
      const response = await restaurantInvoiceAPI.getById(invoiceId);
      setSelectedInvoice(response.data.data || response.data.invoice);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to fetch invoice details:', error);
      alert('Failed to load invoice details. Please try again.');
    }
  };

  const handleDownload = async (invoiceId) => {
    try {
      setDownloadingId(invoiceId);
      const response = await restaurantInvoiceAPI.download(invoiceId);
      console.log('Download response:', response.data);

      // Create a download link - check all possible URL field names
      const responseData = response.data?.data || response.data;
      const url = responseData?.pdfUrl || responseData?.downloadUrl || responseData?.url;

      if (url) {
        // Open in new tab for PDF viewing/download
        window.open(url, '_blank');
      } else {
        console.error('No PDF URL in response:', responseData);
        alert('PDF not available. Please try again later.');
      }
    } catch (error) {
      console.error('Failed to download invoice:', error);
      alert('Failed to download invoice. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const clearDateFilter = () => {
    setDateRange({ startDate: '', endDate: '' });
  };

  const tabs = [
    { id: 'all', label: 'All Invoices' },
    { id: 'subscription', label: 'Subscription' },
    { id: 'advertising', label: 'Advertising' },
    { id: 'platform_fee', label: 'Platform Fee' },
    { id: 'tds', label: 'TDS' },
    { id: 'settlements', label: 'Settlements' },
    { id: 'credit_notes', label: 'Credit Notes' },
    { id: 'debit_notes', label: 'Debit Notes' }
  ];

  return (
    <div style={{
      padding: isMobile ? '16px' : '24px',
      width: '100%',
      boxSizing: 'border-box'
    }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{
            fontSize: isMobile ? '24px' : '32px',
            fontWeight: '700',
            color: theme.textPrimary,
            marginBottom: '8px'
          }}>
            Invoices
          </h1>
          <p style={{
            fontSize: '14px',
            color: theme.textSecondary
          }}>
            View and download your invoices and statements
          </p>
        </div>

        {/* Date Range Filter */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center'
        }}>
          <div style={{ flex: isMobile ? '1 1 100%' : '1 1 200px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: '600',
              color: theme.textSecondary,
              marginBottom: '4px'
            }}>
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                color: theme.textPrimary
              }}
            />
          </div>
          <div style={{ flex: isMobile ? '1 1 100%' : '1 1 200px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: '600',
              color: theme.textSecondary,
              marginBottom: '4px'
            }}>
              End Date
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                color: theme.textPrimary
              }}
            />
          </div>
          {(dateRange.startDate || dateRange.endDate) && (
            <button
              onClick={clearDateFilter}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '8px',
                background: theme.textSecondary,
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: isMobile ? '0' : '18px'
              }}
            >
              Clear Filter
            </button>
          )}
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          background: 'white',
          padding: '4px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: isMobile ? '0 0 auto' : 1,
                padding: isMobile ? '10px 16px' : '12px',
                border: 'none',
                borderRadius: '8px',
                background: activeTab === tab.id ? theme.primaryColor : 'transparent',
                color: activeTab === tab.id ? 'white' : theme.textSecondary,
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Error State */}
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px',
            color: '#ef4444',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '60px 20px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f4f6',
              borderTop: `4px solid ${theme.primaryColor}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }} />
            <p style={{ color: theme.textSecondary }}>Loading invoices...</p>
          </div>
        ) : !Array.isArray(invoices) || invoices.length === 0 ? (
          // Empty State
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '60px 20px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>📄</div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: theme.textPrimary,
              marginBottom: '8px'
            }}>
              No invoices found
            </h3>
            <p style={{ fontSize: '14px', color: theme.textSecondary, marginBottom: '16px' }}>
              {activeTab === 'all' ? 'You don\'t have any invoices yet' : `No ${tabs.find(t => t.id === activeTab)?.label.toLowerCase()} invoices found`}
            </p>
            {activeTab === 'all' && (
              <div style={{
                background: theme.backgroundColor,
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'left',
                maxWidth: '400px',
                margin: '0 auto'
              }}>
                <p style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '8px', fontWeight: '600' }}>
                  Invoices are generated when:
                </p>
                <ul style={{ fontSize: '13px', color: theme.textSecondary, margin: 0, paddingLeft: '20px' }}>
                  <li style={{ marginBottom: '4px' }}>Subscription payments are made</li>
                  <li style={{ marginBottom: '4px' }}>Addon features are purchased</li>
                  <li style={{ marginBottom: '4px' }}>Monthly TDS statements are generated</li>
                  <li>Earnings settlements are processed</li>
                </ul>
              </div>
            )}
          </div>
        ) : (
          // Invoice List
          <div style={{
            display: 'grid',
            gap: '12px'
          }}>
            {invoices.map((invoice) => (
              <div
                key={invoice._id || invoice.id}
                onClick={() => fetchInvoiceDetails(invoice._id || invoice.id)}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: isMobile ? '16px' : '20px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: '1px solid transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.primaryColor;
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 105, 97, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                }}
              >
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: '12px'
                }}>
                  {/* Invoice Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '8px',
                      flexWrap: 'wrap'
                    }}>
                      <h3 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: theme.textPrimary
                      }}>
                        #{invoice.invoiceNumber || invoice.number || 'N/A'}
                      </h3>
                      {invoice.type && (
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: INVOICE_TYPE_COLORS[invoice.type]?.bg || 'rgba(100, 116, 139, 0.1)',
                          color: INVOICE_TYPE_COLORS[invoice.type]?.text || theme.textSecondary
                        }}>
                          {INVOICE_TYPE_LABELS[invoice.type] || invoice.type}
                        </span>
                      )}
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: isMobile ? '8px' : '20px',
                      fontSize: '14px',
                      color: theme.textSecondary,
                      flexWrap: 'wrap'
                    }}>
                      <span>📅 {formatDate(invoice.invoiceDate || invoice.date || invoice.createdAt)}</span>
                      {invoice.dueDate && <span>⏰ Due: {formatDate(invoice.dueDate)}</span>}
                    </div>
                  </div>

                  {/* Amount and Actions */}
                  <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'row' : 'column',
                    alignItems: isMobile ? 'center' : 'flex-end',
                    gap: isMobile ? '12px' : '8px',
                    width: isMobile ? '100%' : 'auto',
                    justifyContent: isMobile ? 'space-between' : 'flex-start'
                  }}>
                    <div style={{
                      fontSize: isMobile ? '18px' : '20px',
                      fontWeight: '700',
                      color: theme.primaryColor
                    }}>
                      {formatCurrency(invoice.taxSummary?.grandTotal || invoice.totalAmount || invoice.amount || 0)}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(invoice._id || invoice.id);
                      }}
                      disabled={downloadingId === (invoice._id || invoice.id)}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '8px',
                        background: theme.primaryColor,
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: downloadingId === (invoice._id || invoice.id) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        opacity: downloadingId === (invoice._id || invoice.id) ? 0.6 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      onMouseEnter={(e) => {
                        if (downloadingId !== (invoice._id || invoice.id)) {
                          e.currentTarget.style.background = theme.primaryDark;
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = theme.primaryColor;
                      }}
                    >
                      {downloadingId === (invoice._id || invoice.id) ? (
                        <>
                          <span style={{
                            width: '14px',
                            height: '14px',
                            border: '2px solid white',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 0.6s linear infinite'
                          }} />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          Download
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Invoice Detail Modal */}
        {showDetailModal && selectedInvoice && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '16px'
            }}
            onClick={() => setShowDetailModal(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '16px',
                maxWidth: '700px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }}
            >
              {/* Modal Header */}
              <div style={{
                padding: '20px 24px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                top: 0,
                background: 'white',
                borderRadius: '16px 16px 0 0',
                zIndex: 1
              }}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: theme.textPrimary
                }}>
                  Invoice Details
                </h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  style={{
                    width: '32px',
                    height: '32px',
                    border: 'none',
                    borderRadius: '8px',
                    background: '#F3F4F6',
                    color: theme.textSecondary,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#E5E7EB';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#F3F4F6';
                  }}
                >
                  ×
                </button>
              </div>

              {/* Modal Content */}
              <div style={{ padding: '24px' }}>
                {/* Invoice Header Info */}
                <div style={{
                  background: theme.backgroundColor,
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                    gap: '16px'
                  }}>
                    <div>
                      <p style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '4px' }}>Invoice Number</p>
                      <p style={{ fontSize: '16px', fontWeight: '600', color: theme.textPrimary }}>
                        #{selectedInvoice.invoiceNumber || selectedInvoice.number || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '4px' }}>Invoice Date</p>
                      <p style={{ fontSize: '16px', fontWeight: '600', color: theme.textPrimary }}>
                        {formatDate(selectedInvoice.invoiceDate || selectedInvoice.date || selectedInvoice.createdAt)}
                      </p>
                    </div>
                    {selectedInvoice.type && (
                      <div>
                        <p style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '4px' }}>Type</p>
                        <span style={{
                          display: 'inline-block',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          background: INVOICE_TYPE_COLORS[selectedInvoice.type]?.bg || 'rgba(100, 116, 139, 0.1)',
                          color: INVOICE_TYPE_COLORS[selectedInvoice.type]?.text || theme.textSecondary
                        }}>
                          {INVOICE_TYPE_LABELS[selectedInvoice.type] || selectedInvoice.type}
                        </span>
                      </div>
                    )}
                    {selectedInvoice.dueDate && (
                      <div>
                        <p style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '4px' }}>Due Date</p>
                        <p style={{ fontSize: '16px', fontWeight: '600', color: theme.textPrimary }}>
                          {formatDate(selectedInvoice.dueDate)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Issuer and Recipient */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: '20px',
                  marginBottom: '20px'
                }}>
                  {selectedInvoice.issuer && (
                    <div>
                      <h3 style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: theme.textSecondary,
                        marginBottom: '12px'
                      }}>
                        From
                      </h3>
                      <div style={{
                        background: theme.backgroundColor,
                        borderRadius: '12px',
                        padding: '16px'
                      }}>
                        <p style={{ fontSize: '15px', fontWeight: '600', color: theme.textPrimary, marginBottom: '4px' }}>
                          {selectedInvoice.issuer.name}
                        </p>
                        {selectedInvoice.issuer.address && (
                          <p style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '4px' }}>
                            {typeof selectedInvoice.issuer.address === 'string'
                              ? selectedInvoice.issuer.address
                              : [selectedInvoice.issuer.address.street, selectedInvoice.issuer.address.city, selectedInvoice.issuer.address.state, selectedInvoice.issuer.address.zipCode].filter(Boolean).join(', ')}
                          </p>
                        )}
                        {selectedInvoice.issuer.gstin && (
                          <p style={{ fontSize: '13px', color: theme.textSecondary }}>
                            GSTIN: {selectedInvoice.issuer.gstin}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {selectedInvoice.recipient && (
                    <div>
                      <h3 style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: theme.textSecondary,
                        marginBottom: '12px'
                      }}>
                        To
                      </h3>
                      <div style={{
                        background: theme.backgroundColor,
                        borderRadius: '12px',
                        padding: '16px'
                      }}>
                        <p style={{ fontSize: '15px', fontWeight: '600', color: theme.textPrimary, marginBottom: '4px' }}>
                          {selectedInvoice.recipient.name}
                        </p>
                        {selectedInvoice.recipient.address && (
                          <p style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '4px' }}>
                            {typeof selectedInvoice.recipient.address === 'string'
                              ? selectedInvoice.recipient.address
                              : [selectedInvoice.recipient.address.street, selectedInvoice.recipient.address.city, selectedInvoice.recipient.address.state, selectedInvoice.recipient.address.zipCode].filter(Boolean).join(', ')}
                          </p>
                        )}
                        {selectedInvoice.recipient.gstin && (
                          <p style={{ fontSize: '13px', color: theme.textSecondary }}>
                            GSTIN: {selectedInvoice.recipient.gstin}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Line Items */}
                {selectedInvoice.lineItems && selectedInvoice.lineItems.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: theme.textSecondary,
                      marginBottom: '12px'
                    }}>
                      Items
                    </h3>
                    <div style={{
                      background: theme.backgroundColor,
                      borderRadius: '12px',
                      overflow: 'hidden'
                    }}>
                      {selectedInvoice.lineItems.map((item, index) => (
                        <div
                          key={index}
                          style={{
                            padding: '16px',
                            borderBottom: index < selectedInvoice.lineItems.length - 1 ? '1px solid #E5E7EB' : 'none',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '14px', fontWeight: '600', color: theme.textPrimary, marginBottom: '4px' }}>
                              {item.description || item.name}
                            </p>
                            {item.quantity && (
                              <p style={{ fontSize: '13px', color: theme.textSecondary }}>
                                Qty: {item.quantity} × {formatCurrency(item.unitPrice || item.price || 0)}
                              </p>
                            )}
                          </div>
                          <p style={{ fontSize: '15px', fontWeight: '600', color: theme.textPrimary }}>
                            {formatCurrency(item.amount || item.total || 0)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tax Breakdown */}
                <div style={{
                  background: theme.backgroundColor,
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <p style={{ fontSize: '14px', color: theme.textSecondary }}>Subtotal</p>
                    <p style={{ fontSize: '14px', fontWeight: '600', color: theme.textPrimary }}>
                      {formatCurrency(selectedInvoice.subtotal || selectedInvoice.baseAmount || 0)}
                    </p>
                  </div>
                  {selectedInvoice.cgst > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <p style={{ fontSize: '14px', color: theme.textSecondary }}>CGST ({getInvoiceTaxRate(selectedInvoice, 'cgst')}%)</p>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: theme.textPrimary }}>
                        {formatCurrency(selectedInvoice.cgst)}
                      </p>
                    </div>
                  )}
                  {selectedInvoice.sgst > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <p style={{ fontSize: '14px', color: theme.textSecondary }}>SGST ({getInvoiceTaxRate(selectedInvoice, 'sgst')}%)</p>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: theme.textPrimary }}>
                        {formatCurrency(selectedInvoice.sgst)}
                      </p>
                    </div>
                  )}
                  {selectedInvoice.igst > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <p style={{ fontSize: '14px', color: theme.textSecondary }}>IGST ({getInvoiceTaxRate(selectedInvoice, 'igst')}%)</p>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: theme.textPrimary }}>
                        {formatCurrency(selectedInvoice.igst)}
                      </p>
                    </div>
                  )}
                  {selectedInvoice.tdsAmount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <p style={{ fontSize: '14px', color: theme.textSecondary }}>TDS ({getInvoiceTaxRate(selectedInvoice, 'tds')}%)</p>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: '#ef4444' }}>
                        - {formatCurrency(selectedInvoice.tdsAmount)}
                      </p>
                    </div>
                  )}
                  <div style={{
                    borderTop: '2px solid #E5E7EB',
                    marginTop: '16px',
                    paddingTop: '16px',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <p style={{ fontSize: '16px', fontWeight: '700', color: theme.textPrimary }}>Total Amount</p>
                    <p style={{ fontSize: '18px', fontWeight: '700', color: theme.primaryColor }}>
                      {formatCurrency(selectedInvoice.taxSummary?.grandTotal || selectedInvoice.totalAmount || selectedInvoice.amount || 0)}
                    </p>
                  </div>
                </div>

                {/* Download Button */}
                <button
                  onClick={() => handleDownload(selectedInvoice._id || selectedInvoice.id)}
                  disabled={downloadingId === (selectedInvoice._id || selectedInvoice.id)}
                  style={{
                    width: '100%',
                    padding: '14px',
                    border: 'none',
                    borderRadius: '12px',
                    background: theme.primaryColor,
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: downloadingId === (selectedInvoice._id || selectedInvoice.id) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: downloadingId === (selectedInvoice._id || selectedInvoice.id) ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (downloadingId !== (selectedInvoice._id || selectedInvoice.id)) {
                      e.currentTarget.style.background = theme.primaryDark;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = theme.primaryColor;
                  }}
                >
                  {downloadingId === (selectedInvoice._id || selectedInvoice.id) ? (
                    <>
                      <span style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid white',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite'
                      }} />
                      Downloading PDF...
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Download PDF
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Add CSS animation for spinner */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

export default Invoices;
